import { useEffect, useState, createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Brand-color + logo scoping.
 *
 * Tenant palette applies ONLY when the user is inside a tenant-scoped
 * surface (coach console, player portal, invite-acceptance):
 *   /dashboard/*, /portal/*, /invite/:token, /team-invite/:token
 *
 * Source of truth (highest → lowest priority):
 *   1. `team.branding.{primary_color, logo_url, display_name}`
 *      → ONLY applied when `dns_verified === true` (Enterprise white-label).
 *      Loaded from `GET /api/teams/me/branding`.
 *   2. Legacy `team.{primary_color, secondary_color, accent_color}`.
 *   3. ArmIQ default palette (navy + red).
 *
 * Also exposes a `useTenantBranding()` hook for components that need the
 * white-label logo URL or display name (header, login splash, etc.).
 */
const ARMIQ_DEFAULT = {
  primary: "#0B1E36",
  secondary: "#D90429",
  accent: "#F8FAFC",
};

const BrandingCtx = createContext({
  logoUrl: null, displayName: null, primaryColor: null,
  isWhiteLabel: false, loading: true, branding: {},
});

export function useTenantBranding() { return useContext(BrandingCtx); }

function isTenantScopedPath(pathname) {
  return (
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/portal") ||
    pathname.startsWith("/invite/") ||
    pathname.startsWith("/team-invite/")
  );
}

export default function BrandProvider({ children }) {
  const { team, user } = useAuth();
  const { pathname } = useLocation();
  const [state, setState] = useState({ loading: true, branding: {}, isWhiteLabel: false });

  // Fetch the new white-label branding payload once per authenticated session.
  useEffect(() => {
    if (!user || user.role === "solo_player" || !user.team_id) {
      setState({ loading: false, branding: {}, isWhiteLabel: false });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/teams/me/branding");
        setState({ loading: false, branding: data.branding || {}, isWhiteLabel: !!data.white_label_enabled });
      } catch {
        setState({ loading: false, branding: {}, isWhiteLabel: false });
      }
    })();
  }, [user]);

  const value = useMemo(() => {
    const b = state.branding || {};
    const ready = state.isWhiteLabel && b.dns_verified;
    return {
      loading: state.loading,
      branding: b,
      isWhiteLabel: state.isWhiteLabel,
      logoUrl: ready ? (b.logo_url || null) : null,
      displayName: ready ? (b.display_name || null) : null,
      primaryColor: ready ? (b.primary_color || null) : null,
    };
  }, [state]);

  // Apply --brand-* CSS variables based on the highest-priority source.
  useEffect(() => {
    const root = document.documentElement;
    if (isTenantScopedPath(pathname)) {
      if (value.primaryColor) {
        // White-label takes precedence (DNS-verified Enterprise tenant)
        root.style.setProperty("--brand-secondary", value.primaryColor);
        root.style.setProperty("--brand-primary",   team?.primary_color   || ARMIQ_DEFAULT.primary);
        root.style.setProperty("--brand-accent",    team?.accent_color    || ARMIQ_DEFAULT.accent);
      } else if (team) {
        // Legacy team palette
        root.style.setProperty("--brand-primary",   team.primary_color   || ARMIQ_DEFAULT.primary);
        root.style.setProperty("--brand-secondary", team.secondary_color || ARMIQ_DEFAULT.secondary);
        root.style.setProperty("--brand-accent",    team.accent_color    || ARMIQ_DEFAULT.accent);
      } else {
        root.style.setProperty("--brand-primary",   ARMIQ_DEFAULT.primary);
        root.style.setProperty("--brand-secondary", ARMIQ_DEFAULT.secondary);
        root.style.setProperty("--brand-accent",    ARMIQ_DEFAULT.accent);
      }
    } else {
      root.style.setProperty("--brand-primary",   ARMIQ_DEFAULT.primary);
      root.style.setProperty("--brand-secondary", ARMIQ_DEFAULT.secondary);
      root.style.setProperty("--brand-accent",    ARMIQ_DEFAULT.accent);
    }
  }, [team, pathname, value.primaryColor]);

  return <BrandingCtx.Provider value={value}>{children}</BrandingCtx.Provider>;
}
